/*
 * ******************************************************
 * Copyright (C) 1994-2021 Babiel GmbH <info@babiel.com>
 *
 * This file is part of a Babiel development
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted without the express
 * permission of Babiel GmbH
 *
 * Any modification is not permitted without the express
 * permission of Babiel GmbH
 *
 * The file may contain confidential and or privileged materials.
 * Any review, retransmission, dissemination or other use of
 * or taking of any action in reliance upon, this file
 * by persons or entities other than the intended is prohibited.
 * ******************************************************
 */

import Swiper, { A11y, Keyboard, Pagination } from 'swiper';
import { Component } from '../../componentSystem/component';
import { BreakpointService } from '../../services/breakpointService';
import { ContactPersonComponentConfig } from './contactPersonComponentConfig';
import {PaginationOptions} from "swiper/types";

Swiper.use([A11y, Keyboard, Pagination]);

export class ContactPersonComponent extends Component {
  protected readonly config: ContactPersonComponentConfig = ContactPersonComponentConfig;
  private swiperInstance: Swiper;

  private get gridOverflow() {
    const { slideClass } = ContactPersonComponentConfig.swiperOptions;

    const slides = this.containerElement.querySelectorAll(`.${slideClass}`);

    switch (slides.length) {
      case 0:
      case 1: return false;
      case 2:
      case 3: return BreakpointService.mediaBreakpointDown('md');
      case 4: return BreakpointService.mediaBreakpointDown('lg');
      default: return true;
    }
  }

  private _paginationBulletIndex: number = null;
  private _previousPaginationBulletIndex: number = null;

  public init() {
    super.init();
    this.swiperInitOrDestroy();
  }

  public setupEvents(): void {
    window.addEventListener('resize', () => {
      this.swiperInitOrDestroy();
    });
  }

  private swiperInitOrDestroy(): void {
    if (this.gridOverflow) {
      if (!this.swiperInstance) {
        this.swiperInit();
      }
    } else {
      if (this.swiperInstance) {
        this.swiperDestroy();
      }
    }
  }

  private swiperInit(): void {
    const { swiperContainerSelector, swiperOptions, noAnimateClass } = ContactPersonComponentConfig;
    const swiperContainer = this.containerElement.querySelector(swiperContainerSelector) as HTMLElement;

    this.containerElement.classList.add(noAnimateClass);
    this.swiperInstance = new Swiper(swiperContainer, swiperOptions);
    this.swiperInstance.on('paginationUpdate', this.onSwiperPaginationUpdate.bind(this));
    this.onSwiperAfterInit();

    window.setTimeout(() => {
      this.containerElement.classList.remove(noAnimateClass);
    });
  }

  private swiperDestroy(): void {
    this.swiperInstance.destroy();
    this.swiperInstance = null;
  }

  private onSwiperAfterInit(): void {
    const { el: paginationEl } = this.swiperInstance.pagination;

    this.setPaginationBulletWasActiveClass(paginationEl);
  }


  private onSwiperPaginationUpdate(swiper: Swiper, paginationEl: HTMLElement): void {
    this.setPaginationBulletWasActiveClass(paginationEl);
  }

  private setPaginationBulletWasActiveClass(paginationEl: HTMLElement): void {
    const { wasActiveClass } = ContactPersonComponentConfig;
    const { bulletClass, bulletActiveClass } = ContactPersonComponentConfig.swiperOptions.pagination as PaginationOptions;
    const bullets = paginationEl.querySelectorAll(`.${bulletClass}`);
    const paginationBulletIndex = Array.from(bullets).findIndex((bullet) =>
      bullet.classList.contains(bulletActiveClass)
    );

    if (this._paginationBulletIndex === paginationBulletIndex) {
      return;
    }

    if (this._previousPaginationBulletIndex !== null) {
      bullets[this._previousPaginationBulletIndex].classList.remove(wasActiveClass);
    }

    if (this._paginationBulletIndex !== null) {
      bullets[this._paginationBulletIndex].classList.add(wasActiveClass);
      this._previousPaginationBulletIndex = this._paginationBulletIndex;
    }

    this._paginationBulletIndex = paginationBulletIndex;
  }
}
